import { 
  doc, 
  setDoc, 
  getDoc, 
  addDoc, 
  collection, 
  query, 
  orderBy, 
  getDocs,
  serverTimestamp,
  Timestamp,
  increment,
  runTransaction
} from 'firebase/firestore';
import { db } from './firebase';
import { removeUndefinedValues } from '../utils/validators';

// Save user profile step 1 - Fixed path structure
export const saveProfileStep1 = async (userId, profileData) => {
  try {
    // Remove undefined values and store profile data directly in the user document
    const cleanData = removeUndefinedValues({
      ...profileData,
      updatedAt: serverTimestamp()
    });
    
    const docRef = doc(db, 'users', userId);
    await setDoc(docRef, cleanData, { merge: true });
    
    return { success: true };
  } catch (error) {
    console.error('Save profile step 1 error:', error);
    return { success: false, error: error.message };
  }
};

// Save user profile step 2 - Fixed path structure
export const saveProfileStep2 = async (userId, profileData) => {
  try {
    // Remove undefined values and store profile data directly in the user document
    const cleanData = removeUndefinedValues({
      ...profileData,
      profileCompleted: true,
      updatedAt: serverTimestamp()
    });
    
    const docRef = doc(db, 'users', userId);
    await setDoc(docRef, cleanData, { merge: true });
    
    return { success: true };
  } catch (error) {
    console.error('Save profile step 2 error:', error);
    return { success: false, error: error.message };
  }
};

// Check if M-Pesa code exists (cost-optimized direct lookup)
export const checkMpesaCodeExists = async (mpesaCode) => {
  try {
    const docRef = doc(db, 'mpesaCodes', mpesaCode);
    const docSnap = await getDoc(docRef);
    
    return { success: true, exists: docSnap.exists() };
  } catch (error) {
    console.error('Check M-Pesa code error:', error);
    return { success: false, error: error.message };
  }
};

// Update total service fees - NEW FUNCTION
export const updateTotalServiceFees = async (amount) => {
  try {
    const statsRef = doc(db, 'admin', 'stats');
    await setDoc(statsRef, {
      totalServiceFees: increment(amount)
    }, { merge: true });
    
    return { success: true };
  } catch (error) {
    console.error('Update service fees error:', error);
    return { success: false, error: error.message };
  }
};

// Get total service fees - NEW FUNCTION
export const getTotalServiceFees = async () => {
  try {
    const statsRef = doc(db, 'admin', 'stats');
    const statsSnap = await getDoc(statsRef);
    
    if (statsSnap.exists()) {
      const data = statsSnap.data();
      return { success: true, total: data.totalServiceFees || 0 };
    } else {
      return { success: true, total: 0 };
    }
  } catch (error) {
    console.error('Get service fees error:', error);
    return { success: false, error: error.message };
  }
};

// Store M-Pesa code to prevent reuse
export const storeMpesaCode = async (mpesaCode, userId, applicationId, amount) => {
  try {
    const cleanData = removeUndefinedValues({
      used: true,
      userId,
      applicationId,
      amount,
      usedAt: serverTimestamp()
    });
    
    const docRef = doc(db, 'mpesaCodes', mpesaCode);
    await setDoc(docRef, cleanData);
    
    return { success: true };
  } catch (error) {
    console.error('Store M-Pesa code error:', error);
    return { success: false, error: error.message };
  }
};

// Create loan application with atomic service fee update - UPDATED FUNCTION
export const createLoanApplication = async (userId, applicationData) => {
  try {
    // Use current timestamp instead of serverTimestamp for immediate availability
    const now = Timestamp.now();
    
    // Remove undefined values before storing
    const cleanData = removeUndefinedValues({
      ...applicationData,
      status: 'pending',
      appliedAt: now // Use Timestamp.now() instead of serverTimestamp()
    });
    
    const applicationsRef = collection(db, 'users', userId, 'applications');
    const docRef = await addDoc(applicationsRef, cleanData);
    
    return { success: true, applicationId: docRef.id };
  } catch (error) {
    console.error('Create application error:', error);
    return { success: false, error: error.message };
  }
};

// Process payment and update service fees atomically - NEW FUNCTION
export const processPaymentAndUpdateFees = async (mpesaCode, userId, applicationData, serviceAmount) => {
  try {
    return await runTransaction(db, async (transaction) => {
      // References
      const mpesaRef = doc(db, 'mpesaCodes', mpesaCode);
      const statsRef = doc(db, 'admin', 'stats');
      const applicationsRef = collection(db, 'users', userId, 'applications');
      
      // Check if M-Pesa code already exists
      const mpesaDoc = await transaction.get(mpesaRef);
      if (mpesaDoc.exists()) {
        throw new Error('M-Pesa code already used');
      }
      
      // Create application
      const now = Timestamp.now();
      const cleanData = removeUndefinedValues({
        ...applicationData,
        status: 'pending',
        appliedAt: now
      });
      
      const newAppRef = doc(applicationsRef);
      transaction.set(newAppRef, cleanData);
      
      // Store M-Pesa code
      const mpesaData = removeUndefinedValues({
        used: true,
        userId,
        applicationId: newAppRef.id,
        amount: serviceAmount,
        usedAt: serverTimestamp()
      });
      transaction.set(mpesaRef, mpesaData);
      
      // Update total service fees
      transaction.set(statsRef, {
        totalServiceFees: increment(serviceAmount)
      }, { merge: true });
      
      return { applicationId: newAppRef.id };
    });
  } catch (error) {
    console.error('Process payment transaction error:', error);
    throw error;
  }
};

// Get user applications
export const getUserApplications = async (userId) => {
  try {
    const applicationsRef = collection(db, 'users', userId, 'applications');
    const q = query(applicationsRef, orderBy('appliedAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const applications = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      applications.push({
        id: doc.id,
        ...data
      });
    });
    
    return { success: true, applications };
  } catch (error) {
    console.error('Get applications error:', error);
    return { success: false, error: error.message };
  }
};

// Get user profile - Fixed path structure
export const getUserProfile = async (userId) => {
  try {
    // Get profile data directly from the user document
    const docRef = doc(db, 'users', userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { success: true, profile: docSnap.data() };
    } else {
      return { success: false, error: 'Profile not found' };
    }
  } catch (error) {
    console.error('Get profile error:', error);
    return { success: false, error: error.message };
  }
};